import type { CSSProperties, MouseEvent } from 'react';
import React from 'react';

import { Box } from '@fhs-legacy/universal-components';
import { STORE_LOCATOR_FOCUS_RESULT_ATTR } from 'pages/store-locator/store-locator-focus-handoff';

import { Body2, StoreCardViewContainer, Subtitle2 } from '../store-card.styled';
import { buildStoreCardDetailsAccessibilityLabel } from '../utils/build-store-card-details-accessibility-label';

import type { StoreCardDetailsProps } from './store-card-details.types';
import { StoreCardError } from './store-card-error';

const webDisclosureButtonReset: CSSProperties = {
  alignItems: 'center',
  appearance: 'none',
  WebkitAppearance: 'none',
  backgroundColor: 'transparent',
  border: 'none',
  color: 'inherit',
  cursor: 'inherit',
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  font: 'inherit',
  justifyContent: 'space-between',
  margin: 0,
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  padding: 0,
  position: 'relative',
  textAlign: 'inherit',
  width: '100%',
};

export const StoreCardDetails: React.VFC<StoreCardDetailsProps> = ({
  body,
  caption,
  errorMessage,
  title,
  isSelected,
  availability,
  address,
  addressAccessibilityLabel,
  isStoreLocatorFocusTarget,
  storeLocatorFocusRef,
  onPress,
  'dd-action-name': ddActionName,
}) => {
  const accessibilityLabel = buildStoreCardDetailsAccessibilityLabel({
    title,
    address,
    addressAccessibilityLabel,
    availability,
    body,
    errorMessage,
    isSelected,
  });
  const expanded = !!isSelected;

  const onWebClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onPress?.();
  };

  return (
    <StoreCardViewContainer accessible={false}>
      <button
        ref={storeLocatorFocusRef}
        type="button"
        aria-expanded={expanded}
        aria-label={accessibilityLabel}
        aria-current={expanded ? 'true' : undefined}
        {...(isStoreLocatorFocusTarget ? { [STORE_LOCATOR_FOCUS_RESULT_ATTR]: 'true' } : {})}
        data-dd-action-name={ddActionName}
        onClick={onWebClick}
        style={webDisclosureButtonReset}
      >
        <Box
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          style={{ width: '100%' }}
          _web={{ 'aria-hidden': true }}
        >
          {title ? <Subtitle2>{title}</Subtitle2> : null}
          {address ? <Box>{address}</Box> : null}
          <Box flexDirection="row" alignItems="center">
            {caption}
          </Box>
          {body ? <Body2>{body}</Body2> : null}
          {errorMessage ? <StoreCardError message={errorMessage} /> : null}
        </Box>
      </button>
    </StoreCardViewContainer>
  );
};
